import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData
} from "@tanstack/react-query";
import { phoneNumbersApi } from "./api";
import { phoneNumberKeys } from "./queryKeys";
import type {
  AssignedPropertiesPayload,
  BuyPhoneNumberPayload,
  ImportFromTwilioPayload,
  SearchAvailableParams,
  UpdatePhoneNumberPayload
} from "./types";

interface UsePhoneNumbersOptions {
  page?: number;
  perPage?: number;
  search?: string;
  enabled?: boolean;
  propertyIds?: number[];
}

export const usePhoneNumbers = ({
  page = 1,
  perPage,
  search,
  enabled = true,
  propertyIds
}: UsePhoneNumbersOptions = {}) =>
  useQuery({
    queryKey: [
      ...phoneNumberKeys.lists(),
      page,
      perPage,
      search ?? "",
      propertyIds ?? []
    ],
    queryFn: () => phoneNumbersApi.list(page, perPage, search, propertyIds),
    enabled,
    placeholderData: keepPreviousData
  });

export const useAllPhoneNumbers = ({
  enabled = true,
  propertyIds
}: { enabled?: boolean; propertyIds?: number[] } = {}) =>
  useQuery({
    queryKey: [...phoneNumberKeys.lists(), "ALL", propertyIds ?? []],
    queryFn: () => phoneNumbersApi.listAll({ propertyIds }),
    enabled,
    placeholderData: keepPreviousData
  });

export const useSearchAvailableNumbers = (
  params: SearchAvailableParams,
  enabled: boolean
) =>
  useQuery({
    queryKey: phoneNumberKeys.searchAvailable(params),
    queryFn: () => phoneNumbersApi.searchAvailable(params),
    enabled,
    placeholderData: keepPreviousData
  });

export const useBuyPhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CREATE"],
    mutationFn: (payload: BuyPhoneNumberPayload) =>
      phoneNumbersApi.buy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useImportFromTwilio = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CREATE"],
    mutationFn: (payload: ImportFromTwilioPayload) =>
      phoneNumbersApi.importFromTwilio(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useUpdatePhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "UPDATE"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: UpdatePhoneNumberPayload;
    }) => phoneNumbersApi.update(commId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useDeletePhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "DELETE"],
    mutationFn: (commId: string) => phoneNumbersApi.remove(commId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const usePropertyPhoneDefaults = (enabled: boolean = true) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefaults(),
    queryFn: () => phoneNumbersApi.listPropertyDefaults(),
    enabled
  });

export const useLeasingAgents = () =>
  useQuery({
    queryKey: phoneNumberKeys.leasingAgents(),
    queryFn: phoneNumbersApi.listLeasingAgents
  });

export const useAssignLeasingAgentPhone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["LEASING_AGENTS", "ASSIGN_PHONE"],
    mutationFn: ({
      leasingAgentId,
      provisionedPhoneNumberId
    }: {
      leasingAgentId: number;
      provisionedPhoneNumberId: number | null;
    }) =>
      phoneNumbersApi.assignLeasingAgentPhone(
        leasingAgentId,
        provisionedPhoneNumberId
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.leasingAgents()
      });
    }
  });
};

export const usePropertyPhoneDefault = (
  propertyId: number,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefault(propertyId),
    queryFn: () => phoneNumbersApi.getPropertyDefault(propertyId),
    enabled
  });

export const useSetPropertyPhoneDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      propertyId,
      phoneNumberId
    }: {
      propertyId: number;
      phoneNumberId: string;
    }) => phoneNumbersApi.setPropertyDefault(propertyId, phoneNumberId),
    onSuccess: (_data, { propertyId }) => {
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefaults()
      });
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefault(propertyId)
      });
    }
  });
};

export const useUpdateAssignedProperties = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "UPDATE_PROPERTIES"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: AssignedPropertiesPayload;
    }) => phoneNumbersApi.updateAssignedProperties(commId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};
